import { notificationsApi } from '@/lib/api/notifications.api'

import type {
  Notification,
  NotificationQueryDto,
  PaginationParams,
} from '@scrambl/shared'
import { isValidUUID } from '@scrambl/shared'
import { SessionChangedError } from '@scrambl/shared/session-scope'

export interface NotificationReaderContext {
  organizationId: string
  userId: string
  signal: AbortSignal
}
export class NotificationReadContractError extends Error {
  constructor() {
    super('Notifications are unavailable')
    this.name = 'NotificationReadContractError'
  }
}
export function isNotificationReadUnavailable(error: unknown) {
  if (
    error instanceof NotificationReadContractError ||
    error instanceof SessionChangedError
  )
    return true
  const status =
    error && typeof error === 'object' && 'status' in error
      ? error.status
      : undefined
  return status === 401 || status === 403 || status === 404
}
export function assertNotificationReaderContext(
  context: NotificationReaderContext,
) {
  context.signal.throwIfAborted()
  if (![context.organizationId, context.userId].every(isValidUUID))
    throw new NotificationReadContractError()
}
export function assertOwnedNotification(
  notification: Notification,
  context: NotificationReaderContext,
) {
  if (
    !notification ||
    !isValidUUID(notification.id) ||
    notification.organizationId !== context.organizationId ||
    notification.recipientId !== context.userId
  )
    throw new NotificationReadContractError()
}
export function assertNotificationCount(value: unknown) {
  if (!Number.isSafeInteger(value) || (value as number) < 0)
    throw new NotificationReadContractError()
}
function rows(value: unknown, context: NotificationReaderContext) {
  if (!Array.isArray(value)) throw new NotificationReadContractError()
  value.forEach(item => assertOwnedNotification(item, context))
}
function countMap(value: unknown) {
  if (!value || typeof value !== 'object' || Array.isArray(value))
    throw new NotificationReadContractError()
  Object.values(value).forEach(assertNotificationCount)
}
export const notificationReadsApi = {
  async list(
    query: NotificationQueryDto & PaginationParams,
    context: NotificationReaderContext,
  ) {
    assertNotificationReaderContext(context)
    const reply = await notificationsApi.getNotifications(query, {
      signal: context.signal,
    })
    assertNotificationReaderContext(context)
    rows(reply?.data, context)
    for (const n of [reply.total, reply.page, reply.limit, reply.totalPages])
      assertNotificationCount(n)
    if (reply.page < 1 || reply.limit < 1)
      throw new NotificationReadContractError()
    return reply
  },
  async detail(id: string, context: NotificationReaderContext) {
    assertNotificationReaderContext(context)
    if (!isValidUUID(id)) throw new NotificationReadContractError()
    const reply = await notificationsApi.getNotification(id, {
      signal: context.signal,
    })
    assertNotificationReaderContext(context)
    assertOwnedNotification(reply, context)
    if (reply.id !== id) throw new NotificationReadContractError()
    return reply
  },
  async unreadCount(context: NotificationReaderContext) {
    assertNotificationReaderContext(context)
    const reply = await notificationsApi.getUnreadCount({
      signal: context.signal,
    })
    assertNotificationReaderContext(context)
    assertNotificationCount(reply?.count)
    return reply
  },
  async summary(context: NotificationReaderContext) {
    assertNotificationReaderContext(context)
    const reply = await notificationsApi.getSummary({ signal: context.signal })
    assertNotificationReaderContext(context)
    assertNotificationCount(reply?.total)
    assertNotificationCount(reply?.unread)
    countMap(reply?.byType)
    countMap(reply?.byPriority)
    return reply
  },
  async preferences(context: NotificationReaderContext) {
    assertNotificationReaderContext(context)
    const reply = await notificationsApi.getPreferences({
      signal: context.signal,
    })
    assertNotificationReaderContext(context)
    if (
      !Array.isArray(reply) ||
      reply.some(
        item =>
          !item ||
          !isValidUUID(item.id) ||
          item.organizationId !== context.organizationId ||
          item.userId !== context.userId,
      )
    )
      throw new NotificationReadContractError()
    return reply
  },
  async feed(context: NotificationReaderContext) {
    assertNotificationReaderContext(context)
    const reply = await notificationsApi.getFeed(undefined, {
      signal: context.signal,
    })
    assertNotificationReaderContext(context)
    for (const lane of [
      reply?.actionRequired,
      reply?.mentions,
      reply?.activity,
    ])
      rows(lane, context)
    for (const n of [
      reply?.counts?.actionRequired,
      reply?.counts?.mentions,
      reply?.counts?.unread,
      reply?.resolvedToday,
    ])
      assertNotificationCount(n)
    return reply
  },
  async unreadEntities(context: NotificationReaderContext) {
    assertNotificationReaderContext(context)
    const reply = await notificationsApi.getUnreadEntities({
      signal: context.signal,
    })
    assertNotificationReaderContext(context)
    if (!reply || !Array.isArray(reply.entities))
      throw new NotificationReadContractError()
    for (const entity of reply.entities) {
      if (
        !entity ||
        !['TASK', 'TICKET'].includes(entity.entityType) ||
        !isValidUUID(entity.entityId) ||
        !Array.isArray(entity.notificationIds) ||
        !entity.notificationIds.every(isValidUUID) ||
        !Array.isArray(entity.types)
      )
        throw new NotificationReadContractError()
      assertNotificationCount(entity.count)
      const instant =
        entity.lastActivityAt instanceof Date
          ? entity.lastActivityAt.getTime()
          : typeof entity.lastActivityAt === 'string'
            ? Date.parse(entity.lastActivityAt)
            : NaN
      if (!Number.isFinite(instant)) throw new NotificationReadContractError()
    }
    return reply
  },
  async markRecordRead(ids: string[], context: NotificationReaderContext) {
    assertNotificationReaderContext(context)
    if (!ids.length || !ids.every(isValidUUID))
      throw new NotificationReadContractError()
    const captured = [...new Set(ids)]
    const reply = await notificationsApi.markMultipleAsRead(captured, {
      signal: context.signal,
    })
    assertNotificationReaderContext(context)
    assertNotificationCount(reply?.updated)
    if (reply.updated > captured.length)
      throw new NotificationReadContractError()
    return reply
  },
}
